import React, { useState, useEffect } from "react"
import {Play, Pause, RotateCcw, SkipForward, SkipBack} from 'lucide-react'

interface VisualizerProps {
  problemType: string
}

interface VisualizationStep {
  dp: number[][]
  currentRow: number
  currentCol: number
  description: string
  highlightCells?: Array<{row: number, col: number}>
}

// Algorithm implementations with step-by-step tracking
const generateFibonacciSteps = (): VisualizationStep[] => {
  const steps: VisualizationStep[] = []
  const n = 8
  const dp = new Array(1).fill(null).map(() => new Array(n + 1).fill(0))
  
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 0,
    description: "Initialize DP array with zeros"
  })
  
  dp[0][0] = 0
  dp[0][1] = 1
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 1,
    description: "Base cases: F(0) = 0, F(1) = 1",
    highlightCells: [{row: 0, col: 0}, {row: 0, col: 1}]
  })
  
  for (let i = 2; i <= n; i++) {
    dp[0][i] = dp[0][i-1] + dp[0][i-2]
    steps.push({
      dp: JSON.parse(JSON.stringify(dp)),
      currentRow: 0,
      currentCol: i,
      description: `F(${i}) = F(${i-1}) + F(${i-2}) = ${dp[0][i-1]} + ${dp[0][i-2]} = ${dp[0][i]}`,
      highlightCells: [{row: 0, col: i}, {row: 0, col: i-1}, {row: 0, col: i-2}]
    })
  }
  
  return steps
}

const generateKnapsackSteps = (): VisualizationStep[] => {
  const steps: VisualizationStep[] = []
  const weights = [2, 3, 4, 5]
  const values = [3, 4, 5, 6]
  const capacity = 8
  const n = weights.length
  
  const dp = new Array(n + 1).fill(null).map(() => new Array(capacity + 1).fill(0))
  
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 0,
    description: `Initialize: Items = [${weights.map((w, i) => `{w:${w},v:${values[i]}}`).join(", ")}], Capacity = ${capacity}`
  })
  
  for (let i = 1; i <= n; i++) {
    for (let w = 0; w <= capacity; w++) {
      if (weights[i-1] <= w) {
        const include = values[i-1] + dp[i-1][w - weights[i-1]]
        const exclude = dp[i-1][w]
        dp[i][w] = Math.max(include, exclude)
        steps.push({
          dp: JSON.parse(JSON.stringify(dp)),
          currentRow: i,
          currentCol: w,
          description: `Item ${i} (w=${weights[i-1]}, v=${values[i-1]}), Capacity=${w}: max(${include}, ${exclude}) = ${dp[i][w]}`,
          highlightCells: [{row: i, col: w}, {row: i-1, col: w}, {row: i-1, col: w - weights[i-1]}]
        })
      } else {
        dp[i][w] = dp[i-1][w]
        steps.push({
          dp: JSON.parse(JSON.stringify(dp)),
          currentRow: i,
          currentCol: w,
          description: `Item ${i} too heavy for capacity ${w}, skip it`,
          highlightCells: [{row: i, col: w}, {row: i-1, col: w}]
        })
      }
    }
  }
  
  return steps
}

const generateLCSSteps = (): VisualizationStep[] => {
  const steps: VisualizationStep[] = []
  const text1 = "AGGTAB"
  const text2 = "GXTXAYB"
  const m = text1.length
  const n = text2.length
  
  const dp = new Array(m + 1).fill(null).map(() => new Array(n + 1).fill(0))
  
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 0,
    description: `Find LCS of "${text1}" and "${text2}"`
  })
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (text1[i-1] === text2[j-1]) {
        dp[i][j] = dp[i-1][j-1] + 1
        steps.push({
          dp: JSON.parse(JSON.stringify(dp)),
          currentRow: i,
          currentCol: j,
          description: `Match: '${text1[i-1]}' == '${text2[j-1]}', LCS[${i}][${j}] = ${dp[i][j]}`,
          highlightCells: [{row: i, col: j}, {row: i-1, col: j-1}]
        })
      } else {
        dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1])
        steps.push({
          dp: JSON.parse(JSON.stringify(dp)),
          currentRow: i,
          currentCol: j,
          description: `No match: '${text1[i-1]}' != '${text2[j-1]}', take max(${dp[i-1][j]}, ${dp[i][j-1]}) = ${dp[i][j]}`,
          highlightCells: [{row: i, col: j}, {row: i-1, col: j}, {row: i, col: j-1}]
        })
      }
    }
  }
  
  return steps
}

const generateStairsSteps = (): VisualizationStep[] => {
  const steps: VisualizationStep[] = []
  const n = 8
  const dp = new Array(1).fill(null).map(() => new Array(n + 1).fill(0))
  
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 0,
    description: "Ways to climb stairs (1 or 2 steps at a time)"
  })
  
  dp[0][0] = 1
  dp[0][1] = 1
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 1,
    description: "Base: 1 way to climb 0 stairs, 1 way to climb 1 stair",
    highlightCells: [{row: 0, col: 0}, {row: 0, col: 1}]
  })
  
  for (let i = 2; i <= n; i++) {
    dp[0][i] = dp[0][i-1] + dp[0][i-2]
    steps.push({
      dp: JSON.parse(JSON.stringify(dp)),
      currentRow: 0,
      currentCol: i,
      description: `Ways(${i}) = Ways(${i-1}) + Ways(${i-2}) = ${dp[0][i-1]} + ${dp[0][i-2]} = ${dp[0][i]}`,
      highlightCells: [{row: 0, col: i}, {row: 0, col: i-1}, {row: 0, col: i-2}]
    })
  }
  
  return steps
}

const generateCoinChangeSteps = (): VisualizationStep[] => {
  const steps: VisualizationStep[] = []
  const coins = [1, 2, 5]
  const amount = 11
  const n = coins.length
  
  const dp = new Array(n + 1).fill(null).map(() => new Array(amount + 1).fill(Infinity))
  
  for (let i = 0; i <= n; i++) {
    dp[i][0] = 0
  }
  
  steps.push({
    dp: JSON.parse(JSON.stringify(dp)),
    currentRow: 0,
    currentCol: 0,
    description: `Coins = [${coins.join(", ")}], Target = ${amount}. Base: 0 coins for amount 0`
  })
  
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= amount; j++) {
      dp[i][j] = dp[i-1][j]
      
      if (j >= coins[i-1]) {
        dp[i][j] = Math.min(dp[i][j], dp[i][j - coins[i-1]] + 1)
        steps.push({
          dp: JSON.parse(JSON.stringify(dp)),
          currentRow: i,
          currentCol: j,
          description: `Coin ${coins[i-1]}: amount ${j}, min(${dp[i-1][j]}, ${dp[i][j - coins[i-1]]} + 1) = ${dp[i][j] === Infinity ? '∞' : dp[i][j]}`,
          highlightCells: [{row: i, col: j}, {row: i-1, col: j}, {row: i, col: j - coins[i-1]}]
        })
      } else {
        steps.push({
          dp: JSON.parse(JSON.stringify(dp)),
          currentRow: i,
          currentCol: j,
          description: `Coin ${coins[i-1]} > amount ${j}, skip`,
          highlightCells: [{row: i, col: j}, {row: i-1, col: j}]
        })
      }
    }
  }
  
  return steps
}

export default function Visualizer({ problemType }: VisualizerProps) {
  const [steps, setSteps] = useState<VisualizationStep[]>([])
  const [currentStep, setCurrentStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
    let newSteps: VisualizationStep[] = []
    
    switch (problemType) {
      case "fibonacci":
        newSteps = generateFibonacciSteps()
        break
      case "knapsack":
        newSteps = generateKnapsackSteps()
        break
      case "lcs":
        newSteps = generateLCSSteps()
        break
      case "stairs":
        newSteps = generateStairsSteps()
        break
      case "coinchange":
        newSteps = generateCoinChangeSteps()
        break
      default:
        newSteps = generateFibonacciSteps()
    }
    
    setSteps(newSteps)
    setCurrentStep(0)
    setIsPlaying(false)
  }, [problemType])

  useEffect(() => {
    if (isPlaying && currentStep < steps.length - 1) {
      const timer = setTimeout(() => {
        setCurrentStep(currentStep + 1)
      }, 800)
      return () => clearTimeout(timer)
    } else if (currentStep >= steps.length - 1) {
      setIsPlaying(false)
    }
  }, [isPlaying, currentStep, steps.length])

  const handlePlay = () => setIsPlaying(!isPlaying)
  const handleReset = () => {
    setCurrentStep(0)
    setIsPlaying(false)
  }
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
      setIsPlaying(false)
    }
  }
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      setIsPlaying(false)
    }
  }

  if (steps.length === 0) return null

  const currentVisualization = steps[currentStep]
  const isSingleRow = currentVisualization.dp.length === 1

  const isHighlighted = (row: number, col: number) => {
    return currentVisualization.highlightCells?.some(
      cell => cell.row === row && cell.col === col
    ) || false
  }

  const isCurrent = (row: number, col: number) => {
    return row === currentVisualization.currentRow && col === currentVisualization.currentCol
  }

  return (
    <section className="mb-12">
      <h2 className="text-3xl font-bold mb-6">Step-by-Step Visualization</h2>
      
      <div className="bg-slate-900 rounded-xl border border-slate-700 p-8">
        {/* Description */}
        <div className="mb-6 p-4 bg-slate-800 rounded-lg border border-slate-700">
          <p className="text-cyan-300 font-mono text-sm">
            {currentVisualization.description}
          </p>
        </div>

        {/* DP Table */}
        <div className="bg-slate-950 rounded-lg p-8 mb-6 overflow-x-auto border border-slate-800">
          <div className={`inline-block ${isSingleRow ? 'min-w-full' : ''}`}>
            <table className="border-collapse">
              <tbody>
                {currentVisualization.dp.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, colIndex) => {
                      const highlighted = isHighlighted(rowIndex, colIndex)
                      const current = isCurrent(rowIndex, colIndex)
                      const value = cell === Infinity ? '∞' : cell
                      
                      return (
                        <td
                          key={colIndex}
                          className={`
                            w-16 h-16 text-center border-2 font-mono font-bold text-lg
                            transition-all duration-300
                            ${current 
                              ? 'bg-cyan-500 text-white border-cyan-400 scale-110 shadow-lg shadow-cyan-500/50' 
                              : highlighted
                              ? 'bg-emerald-500/30 border-emerald-400 text-emerald-200'
                              : value === 0 || value === '∞'
                              ? 'bg-slate-800 border-slate-700 text-slate-600'
                              : 'bg-slate-700 border-slate-600 text-white'
                            }
                          `}
                        >
                          {value}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              className="p-3 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={currentStep === 0}
            >
              <SkipBack className="w-5 h-5" />
            </button>
            
            <button
              onClick={handlePlay}
              className="p-3 bg-cyan-500 hover:bg-cyan-600 rounded-lg transition-colors"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            
            <button
              onClick={handleNext}
              className="p-3 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={currentStep === steps.length - 1}
            >
              <SkipForward className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleReset}
              className="p-3 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors ml-2"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>

          <div className="text-slate-400">
            Step <span className="text-white font-semibold">{currentStep + 1}</span> / {steps.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 bg-slate-800 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </section>
  )
}